import type { ImportInfo } from '@mcgames/contracts';
import { Button } from '@mcgames/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { useImportAction } from '../imports-hooks.ts';
import styles from './Games.module.css';

export type DuplicateActionsProps = {
  info: ImportInfo;
  size?: 'sm';
  /** Runs after the discard succeeded, with the import as it is now. */
  onDone?: (result: ImportInfo) => void;
};

/**
 * The way out of an import that stopped because its game is already in the catalog: discard it
 * (its draft goes too), after asking first.
 */
export function DuplicateActions({ info, size, onDone }: DuplicateActionsProps) {
  const { t } = useTranslation('admin');
  const discard = useImportAction(info, 'discard');
  const [asking, setAsking] = useState(false);

  return (
    <>
      <div className={styles['row']}>
        <Button variant="secondary" size={size} onClick={() => setAsking(true)}>
          {t('games.import.discard')}
        </Button>
      </div>
      {asking && (
        <ConfirmDialog
          title={t('games.import.discardTitle')}
          message={t('games.import.discardMessage')}
          confirmLabel={t('games.import.discard')}
          tone="danger"
          pending={discard.isPending}
          error={discard.error}
          onConfirm={() =>
            discard.mutate(undefined, {
              onSuccess: (result) => {
                setAsking(false);
                onDone?.(result);
              },
            })
          }
          onCancel={() => {
            discard.reset();
            setAsking(false);
          }}
        />
      )}
    </>
  );
}
